import { CareServiceConfig } from '@app/icare/entities/careService/config'
import { patientCareServiceQueries } from '@app/icare/entities/careService/queries/patientCareServiceQueries'
import { PatientCareServiceEditDrawer } from '@app/icare/entities/patient/edits/PatientCareServiceEditDrawer'
import { patientFindingsQueryOptions } from '@app/icare/entities/patient/queries/patientFindingsQueryOptions'
import type { PatientCareServiceRelation } from '@app/icare/entities/patient/schema/PatientCareService'
import { PatientId } from '@app/icare/entities/patient/schema/PatientId'
import { RelationTabs } from '@app/icare/routes/_app/patienten/$id/_edit/-components/RelationTabs'
import { isActivePatientRelation } from '@app/icare/shared/api/form.type'
import { t } from '@lingui/core/macro'
import { entityParams } from '@pkg/db/utils'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useRelationCommitNavigation } from '../-components/useRelationCommitNavigation'

export const Route = createFileRoute('/_app/patienten/$id/_edit/_careService/pflegedienste')({
  params: entityParams({ id: PatientId }),
  loader: ({ params, context: { queryClient } }) =>
    Promise.all([
      queryClient.ensureQueryData(patientCareServiceQueries.forTabs(params.id)),
      queryClient.ensureQueryData(patientFindingsQueryOptions(params.id)),
    ]),
  component: RouteComponent,
})

function RouteComponent() {
  const { id } = Route.useParams()
  const navigate = Route.useNavigate()
  const { data: relations = [] } = useSuspenseQuery(patientCareServiceQueries.forTabs(id))
  const { data: findings = [] } = useQuery(patientFindingsQueryOptions(id))
  const { data: totalCount } = useQuery(patientCareServiceQueries.totalCount(id))

  const handleAfterCommit = useRelationCommitNavigation<PatientCareServiceRelation>({
    isInactive: relation => !isActivePatientRelation(relation),
    onCreateNavigate: relation =>
      navigate({
        to: '/patienten/$id/pflegedienste/$relation',
        params: { id, relation: relation.id },
      }),
    onInactiveEditNavigate: () => navigate({ to: '.', replace: true }),
  })

  return (
    <RelationTabs
      relations={relations}
      relationTotalCount={totalCount}
      basePath={`/patienten/${id}/pflegedienste`}
      findings={findings}
      findingsGroup="careServices"
      getLabel={item => CareServiceConfig.format.primary(item.to) ?? t`Pflegedienst`}
      onAdd={params =>
        void PatientCareServiceEditDrawer.show({ patientId: id, onAfterCommit: handleAfterCommit, ...params })
      }
      entity="careService"
    />
  )
}
